<template>
    <div class="relative">
        <!-- Group title -->
        <span v-if="showGroupTitle" class="text-lg font-bold" v-text="groupTitle" />

        <!-- Table -->
        <table class="mt-2 w-full">
            <thead>
                <tr class="text-left">
                    <th class="w-full pr-4 font-semibold" v-text="$t(`general.price_tables.title.${tableType}`)"></th>
                    <th v-if="showRecommendedPriceColumn" class="pr-5 text-right font-semibold whitespace-nowrap">
                        <span v-if="hasAnyDiscountedRow" v-text="$t('general.prices.starting_from.label')" />
                    </th>
                    <th class="text-right font-semibold whitespace-nowrap" v-text="$t('general.prices.for.label')" />
                </tr>
            </thead>
            <tbody :class="{ 'divide-y divide-gray-100': !hideDividers }">
                <!-- Row for each product in the group -->
                <tr v-for="{ configurableProductProduct, label } in displayableRows" :key="`group_${configurableProductProduct.product_id}`">
                    <td class="py-2 pr-2">
                        <span
                            v-if="isAmountTableType"
                            v-text="
                                $t('general.price_tables.entries.amount_label', {
                                    count: Number(configurableProductProduct.metadata.price_table_entry?.product_label),
                                })
                            "
                        />
                        <span v-else v-text="label" />
                    </td>
                    <td v-if="showRecommendedPriceColumn" class="py-2 pr-5 text-right whitespace-nowrap">
                        <span
                            v-if="configurableProductProduct.product.data.product_price_recommended"
                            class="line-through"
                            v-text="formatPrice(configurableProductProduct.product.data.product_price_recommended)"
                        />
                        <span v-else />
                    </td>
                    <td
                        class="py-2 text-right font-bold whitespace-nowrap text-quaternary"
                        v-text="formatPrice(configurableProductProduct.product.data.product_price)"
                    />
                </tr>
            </tbody>
        </table>

        <!-- If there are more than the threshold products, show a button to show all products -->
        <template v-if="rows.length > productLengthThreshold">
            <div
                :class="[
                    'absolute bottom-[30px] h-16 w-full bg-gradient-to-t from-white transition',
                    isShowingAllProducts ? 'opacity-0' : 'opacity-100',
                ]"
            />
            <button class="mt-2 cursor-pointer text-quaternary underline outline-none" @click="isShowingAllProducts = !isShowingAllProducts">
                <span v-if="isShowingAllProducts">{{ $t('general.price_table.filters.show_less') }}</span>
                <span v-else>{{ $t('general.price_table.filters.show_more') }}</span>
            </button>
        </template>
    </div>
</template>

<script lang="ts" setup>
import { useFormatPrice } from '@/composables/useFormatPrice';
import {
    getPriceTableDisplayLabel,
    getPriceTableSortKey,
    getPriceTableUniqueKey,
    collapsePriceTableRowsWithSamePrice,
    type PriceTableRow,
} from '@/lib/products/priceTable';
import { type ConfigurableProductProductObject, ConfigurableProductProductPriceTableEntryTypeEnum } from '@/types/generated';
import _ from 'lodash';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

type ProductPriceTableGroupProps = {
    productGroups: ConfigurableProductProductObject[];
    groupTitle: string;
    showGroupTitle?: boolean;
    hideDividers?: boolean;
};

const props = withDefaults(defineProps<ProductPriceTableGroupProps>(), {
    showGroupTitle: true,
    hideDividers: false,
});

const formatPrice = useFormatPrice();
const { t } = useI18n();

const productLengthThreshold = 4;
const isShowingAllProducts = ref(false);
const sortDirections: ['asc', 'asc', 'asc'] = ['asc', 'asc', 'asc'];

type SortablePriceTableProduct = {
    configurableProductProduct: ConfigurableProductProductObject;
    sortKey: [number, number, string];
};

function toSortablePriceTableProduct(configurableProductProduct: ConfigurableProductProductObject): SortablePriceTableProduct {
    return {
        configurableProductProduct,
        sortKey: getPriceTableSortKey(configurableProductProduct),
    };
}

function getPrimarySortValue(sortablePriceTableProduct: SortablePriceTableProduct): number {
    return sortablePriceTableProduct.sortKey[0];
}

function getSecondarySortValue(sortablePriceTableProduct: SortablePriceTableProduct): number {
    return sortablePriceTableProduct.sortKey[1];
}

function getTertiarySortValue(sortablePriceTableProduct: SortablePriceTableProduct): string {
    return sortablePriceTableProduct.sortKey[2];
}

// Order by width and height
const orderedProducts = computed(() =>
    _(props.productGroups)
        .map(toSortablePriceTableProduct)
        .orderBy([getPrimarySortValue, getSecondarySortValue, getTertiarySortValue], sortDirections)
        .map('configurableProductProduct')
        .value(),
);

// Get unique products based on the width and height
const uniqueProducts = computed(() => _.uniqBy(orderedProducts.value, getPriceTableUniqueKey));

// Sizes that all cost the same collapse into one row; amounts always keep a row each.
const rows = computed<PriceTableRow[]>(() => {
    if (isAmountTableType.value) {
        return _.map(uniqueProducts.value, (configurableProductProduct: ConfigurableProductProductObject) => ({
            configurableProductProduct,
            label: getDisplayLabel(configurableProductProduct),
        }));
    }

    return collapsePriceTableRowsWithSamePrice(uniqueProducts.value, t('general.price_tables.entries.all_sizes'));
});

// Get only the first rows based on the threshold
const displayableRows = computed(() => _.take(rows.value, isShowingAllProducts.value ? rows.value.length : productLengthThreshold));

const tableType = computed(
    () => _.first(uniqueProducts.value)?.metadata.price_table_entry?.type ?? ConfigurableProductProductPriceTableEntryTypeEnum.SIZE,
);

const isAmountTableType = computed(() => tableType.value === ConfigurableProductProductPriceTableEntryTypeEnum.AMOUNT);

// True when at least one product in this group has a recommended price set.
const hasAnyRecommendedPrice = computed<boolean>(() =>
    uniqueProducts.value.some(
        (configurableProductProduct: ConfigurableProductProductObject) =>
            (configurableProductProduct.product.data.product_price_recommended ?? 0) > 0,
    ),
);

// Only show the "Vanaf" header when at least one row is actually discounted (recommended > price).
const hasAnyDiscountedRow = computed<boolean>(() =>
    uniqueProducts.value.some(
        (configurableProductProduct: ConfigurableProductProductObject) =>
            (configurableProductProduct.product.data.product_price_recommended ?? 0) > configurableProductProduct.product.data.product_price,
    ),
);

// Hide the recommended-price column only when no product in this group has a recommended price to show.
const showRecommendedPriceColumn = computed<boolean>(() => hasAnyRecommendedPrice.value);

function getDisplayLabel(configurableProductProduct: ConfigurableProductProductObject): string | undefined {
    return getPriceTableDisplayLabel(configurableProductProduct);
}
</script>
